/**
 * Custom schema for profile 2D top view previews
 * Renders multiple profile top views as lines with segment lengths
 */

// Cross-section is now rendered separately above the 3D preview (profileCrossSectionSchema)
// Imports for cross-section drawing removed

import { calculateCommonProfileCenterValue } from "../utils/calculateProfileCenterValue";
import { determineProfileDisplay } from "../utils/profileDisplayLogic";
import {
  boundingBoxFrom2DProfiles,
  calculateScale,
  calculateTextAngle,
  calculateTextPosition,
  distance2D,
  PROFILE_PREVIEW_MM_TO_PT,
  PROFILE_PREVIEW_OUTER_PADDING_PT,
  PROFILE_PREVIEW_SEGMENT_LABEL_OFFSET_PT,
  profilePreviewInnerSizeMm,
  projectProfilePointsTo2D,
} from "../utils/profilePreviewGeometry";

/**
 * Profile preview entry with 2D projection points
 */
interface ProfilePreviewEntry {
  points: Array<[number, number, number]>; // 3D points [x, y, z]
  label?: string; // Optional label (e.g., "Oben", "Unten")
  cutReference?: number; // Sides enum value to determine projection plane
  crossSection?: string; // SVG path for cross-section preview
  count?: string; // Count (e.g., "2")
}

/**
 * Calculate center value for profiles with >=3 segments
 * Uses shared utility function for consistent calculation
 */
function calculateCenterValue(
  allPoints2D: Array<{
    points: Array<[number, number]>;
    profile: ProfilePreviewEntry;
  }>,
): number | null {
  // Prepare profiles for common calculation
  // Calculate segment lengths from 2D points (already in mm)
  const profilesForCalculation = allPoints2D.map(({ points, profile }) => {
    const segmentLengths: number[] = [];
    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];
      if (!p1 || !p2) continue;
      segmentLengths.push(distance2D(p1, p2));
    }
    return {
      pointCount: profile.points.length,
      segmentLengths,
    };
  });

  // Use shared utility function
  return calculateCommonProfileCenterValue(profilesForCalculation);
}

// =============================================================================
// LINE SEGMENT DRAWING HELPERS
// =============================================================================

/** Stroke width (pt) for Schließprofil 2D top-view contour in PDF */
const PROFILE_PREVIEW_CONTOUR_STROKE_PT = 1.5;

interface DrawLineSegmentParams {
  page: import("pdf-lib").PDFPage;
  rgb: (r: number, g: number, b: number) => import("pdf-lib").RGB;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  thickness: number;
}

/**
 * Draw a line segment on the PDF page
 */
function drawLineSegment(params: DrawLineSegmentParams): void {
  const { page, rgb, x1, y1, x2, y2, thickness } = params;
  page.drawLine({
    start: { x: x1, y: y1 },
    end: { x: x2, y: y2 },
    thickness,
    color: rgb(0, 0, 0),
  });
}

interface EndCapParams {
  page: import("pdf-lib").PDFPage;
  rgb: (r: number, g: number, b: number) => import("pdf-lib").RGB;
  x: number;
  y: number;
  perpX: number;
  perpY: number;
  endCapLength: number;
  thickness: number;
}

/**
 * Draw an end cap perpendicular line
 */
function drawEndCap(params: EndCapParams): void {
  const { page, rgb, x, y, perpX, perpY, endCapLength, thickness } = params;
  page.drawLine({
    start: { x: x + perpX * endCapLength, y: y + perpY * endCapLength },
    end: { x: x - perpX * endCapLength, y: y - perpY * endCapLength },
    thickness,
    color: rgb(0, 0, 0),
  });
}

// =============================================================================
// SEGMENT RENDERING
// =============================================================================

interface SegmentRenderContext {
  page: import("pdf-lib").PDFPage;
  rgb: (r: number, g: number, b: number) => import("pdf-lib").RGB;
  font: import("pdf-lib").PDFFont;
  fontSize: number;
  endCapLength: number;
  centerX: number;
  centerY: number;
  bboxCenterX: number;
  bboxCenterY: number;
  scale: number;
}

/**
 * Draw a single profile segment with end caps and label
 */
async function drawProfileSegment(
  ctx: SegmentRenderContext,
  p1: [number, number],
  p2: [number, number],
  isFirstSegment: boolean,
  isLastSegment: boolean,
): Promise<void> {
  const {
    page,
    rgb,
    font,
    fontSize,
    endCapLength,
    centerX,
    centerY,
    bboxCenterX,
    bboxCenterY,
    scale,
  } = ctx;
  const { degrees } = await import("pdf-lib");

  // Transform to PDF coordinates
  const x1 = centerX + (p1[0] - bboxCenterX) * scale;
  const y1 = centerY - (p1[1] - bboxCenterY) * scale;
  const x2 = centerX + (p2[0] - bboxCenterX) * scale;
  const y2 = centerY - (p2[1] - bboxCenterY) * scale;

  // Draw main line segment
  drawLineSegment({
    page,
    rgb,
    x1,
    y1,
    x2,
    y2,
    thickness: PROFILE_PREVIEW_CONTOUR_STROKE_PT,
  });

  // Draw end caps
  const capDx = x2 - x1;
  const capDy = y2 - y1;
  const capLen = Math.hypot(capDx, capDy);

  if (capLen > 0) {
    const capPerpX = -capDy / capLen;
    const capPerpY = capDx / capLen;

    if (isFirstSegment) {
      drawEndCap({
        page,
        rgb,
        x: x1,
        y: y1,
        perpX: capPerpX,
        perpY: capPerpY,
        endCapLength,
        thickness: PROFILE_PREVIEW_CONTOUR_STROKE_PT,
      });
    }
    if (isLastSegment) {
      drawEndCap({
        page,
        rgb,
        x: x2,
        y: y2,
        perpX: capPerpX,
        perpY: capPerpY,
        endCapLength,
        thickness: PROFILE_PREVIEW_CONTOUR_STROKE_PT,
      });
    }
  }

  // Draw segment length label
  const segmentLength = distance2D(p1, p2);
  const lengthText = `${Math.round(segmentLength)}`;
  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const segLength = Math.hypot(dx, dy);

  if (segLength > 0) {
    const { angleRad, angleDeg, flipOffset } = calculateTextAngle(dx, dy);
    const perpX = flipOffset ? dy / segLength : -dy / segLength;
    const perpY = flipOffset ? -dx / segLength : dx / segLength;
    const textWidth = font.widthOfTextAtSize(lengthText, fontSize);
    const textPos = calculateTextPosition({
      midX,
      midY,
      perpX,
      perpY,
      textWidth,
      fontSize,
      angleRad,
      offset: PROFILE_PREVIEW_SEGMENT_LABEL_OFFSET_PT,
    });

    if (textPos) {
      page.drawText(lengthText, {
        x: textPos.x,
        y: textPos.y,
        size: fontSize,
        font,
        color: rgb(0, 0, 0),
        rotate: degrees(angleDeg),
      });
    }
  }
}

/**
 * Draw all segments for a profile group
 */
async function drawProfileGroupSegments(
  ctx: SegmentRenderContext,
  allPoints2D: Array<{ points: Array<[number, number]> }>,
): Promise<void> {
  for (const { points: points2D } of allPoints2D) {
    for (let i = 0; i < points2D.length - 1; i++) {
      const p1 = points2D[i];
      const p2 = points2D[i + 1];
      if (p1 && p2) {
        await drawProfileSegment(
          ctx,
          p1,
          p2,
          i === 0,
          i === points2D.length - 2,
        );
      }
    }
  }
}

// =============================================================================
// MAIN PROFILE PREVIEW RENDERER
// =============================================================================

/**
 * Parse and validate profile preview data
 */
function parseProfileData(value: string): ProfilePreviewEntry[] | null {
  if (!value || value.trim().length === 0) return null;

  try {
    const profiles = JSON.parse(value);
    if (!Array.isArray(profiles)) return null;
    return profiles.filter(
      (p: ProfilePreviewEntry) =>
        p.points && Array.isArray(p.points) && p.points.length >= 2,
    );
  } catch {
    return null;
  }
}

/**
 * Project profiles to 2D points
 */
function projectProfilesToPoints(
  profiles: ProfilePreviewEntry[],
): Array<{ points: Array<[number, number]>; profile: ProfilePreviewEntry }> {
  const allPoints2D: Array<{
    points: Array<[number, number]>;
    profile: ProfilePreviewEntry;
  }> = [];
  for (const profile of profiles) {
    const points2D = projectProfilePointsTo2D(
      profile.points,
      profile.cutReference,
    );
    if (points2D.length >= 2) {
      allPoints2D.push({ points: points2D, profile });
    }
  }
  return allPoints2D;
}

/**
 * Draw profile 2D top view previews
 */
async function drawProfilePreviews(arg: {
  pdfDoc: import("pdf-lib").PDFDocument;
  page: import("pdf-lib").PDFPage;
  schema: { position: { x: number; y: number }; width: number; height: number };
  value: string;
}): Promise<void> {
  const { rgb, StandardFonts } = await import("pdf-lib");

  const validProfiles = parseProfileData(arg.value);
  if (!validProfiles || validProfiles.length === 0) return;

  // Use shared utility function for consistent display logic
  const displayResult = determineProfileDisplay(
    validProfiles,
    (p) => p.cutReference,
  );

  const { x, y } = arg.schema.position;
  const { width, height } = arg.schema;
  const pageHeight = arg.page.getHeight();
  const startX = x * PROFILE_PREVIEW_MM_TO_PT;
  const startY = pageHeight - y * PROFILE_PREVIEW_MM_TO_PT;

  const font = await arg.pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await arg.pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const fontSize = 10;

  // If more than 2 cutReferences, show "Siehe Case"
  if (displayResult.showSieheCase) {
    const sieheText = "Siehe Case";
    const sieheFontSize = 14;
    const textWidth = fontBold.widthOfTextAtSize(sieheText, sieheFontSize);
    arg.page.drawText(sieheText, {
      x: startX + (width * PROFILE_PREVIEW_MM_TO_PT) / 2 - textWidth / 2,
      y: startY - (height * PROFILE_PREVIEW_MM_TO_PT) / 2 - sieheFontSize / 2,
      size: sieheFontSize,
      font: fontBold,
      color: rgb(0, 0, 0),
    });
    return;
  }

  const padding = PROFILE_PREVIEW_OUTER_PADDING_PT;
  const groupWidth = width * PROFILE_PREVIEW_MM_TO_PT - padding * 2;
  const groupHeight = height * PROFILE_PREVIEW_MM_TO_PT - padding * 2;
  const groupX = startX + padding;
  const groupY = startY - padding;

  // Draw only the first group
  if (displayResult.profilesToDisplay.length === 0) return;
  const profilesInGroup = displayResult.profilesToDisplay;

  const allPoints2D = projectProfilesToPoints(profilesInGroup);
  if (allPoints2D.length === 0) return;

  const bbox = boundingBoxFrom2DProfiles(allPoints2D);
  if (bbox.width <= 0 && bbox.height <= 0) return;

  const { maxW: maxWmm, maxH: maxHmm } = profilePreviewInnerSizeMm(
    width,
    height,
  );
  const scale = calculateScale(
    bbox.width,
    bbox.height,
    maxWmm * PROFILE_PREVIEW_MM_TO_PT,
    maxHmm * PROFILE_PREVIEW_MM_TO_PT,
  );

  const centerX = groupX + groupWidth / 2;
  const centerY = groupY - groupHeight / 2;
  const bboxCenterX = (bbox.minX + bbox.maxX) / 2;
  const bboxCenterY = (bbox.minY + bbox.maxY) / 2;

  const ctx: SegmentRenderContext = {
    page: arg.page,
    rgb,
    font,
    fontSize,
    endCapLength: 4,
    centerX,
    centerY,
    bboxCenterX,
    bboxCenterY,
    scale,
  };

  await drawProfileGroupSegments(ctx, allPoints2D);

  // Draw center length label (only for profiles with >=3 segments, all same length)
  const centerValue = calculateCenterValue(allPoints2D);
  if (centerValue !== null) {
    // Konvertiere von mm zu cm (geteilt durch 10) und runde auf 1 Dezimalstelle
    const displayValue = (Math.floor(centerValue) / 10).toFixed(1);
    const totalLengthText = `| ${displayValue} |`;
    const textWidth = fontBold.widthOfTextAtSize(totalLengthText, fontSize);
    arg.page.drawText(totalLengthText, {
      x: centerX - textWidth / 2,
      y: centerY - fontSize / 2,
      size: fontSize,
      font: fontBold,
      color: rgb(0, 0, 0),
    });
  }
}

export const profilePreviewSchema = {
  ui: (arg: { rootElement: HTMLElement; value: string }) => {
    const div = document.createElement("div");
    div.style.width = "100%";
    div.style.height = "100%";
    div.style.backgroundColor = "#f5f5f5";
    div.style.display = "flex";
    div.style.alignItems = "center";
    div.style.justifyContent = "center";
    div.style.gap = "8px";
    div.style.padding = "4px";
    div.style.fontSize = "10px";
    div.style.color = "#666";

    try {
      const profiles: ProfilePreviewEntry[] = JSON.parse(arg.value || "[]");
      profiles.forEach((profile) => {
        if (profile.points && profile.points.length >= 2) {
          const profileDiv = document.createElement("div");
          profileDiv.style.display = "flex";
          profileDiv.style.flexDirection = "column";
          profileDiv.style.alignItems = "center";
          profileDiv.style.gap = "4px";

          const svg = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "svg",
          );
          svg.setAttribute("width", "100%");
          svg.setAttribute("height", "100%");
          svg.setAttribute("viewBox", "0 0 100 100");
          svg.style.border = "1px solid #ccc";

          // Project and draw lines
          const points2D = projectProfilePointsTo2D(
            profile.points,
            profile.cutReference,
          );
          const path = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "path",
          );
          path.setAttribute("fill", "none");
          path.setAttribute("stroke", "#000");
          path.setAttribute("stroke-width", "0.5");
          if (points2D.length > 0) {
            const p0 = points2D[0];
            if (p0) {
              let pathData = `M ${p0[0]} ${p0[1]}`;
              for (let i = 1; i < points2D.length; i++) {
                const pi = points2D[i];
                if (pi) {
                  pathData += ` L ${pi[0]} ${pi[1]}`;
                }
              }
              path.setAttribute("d", pathData);
            } else {
              path.setAttribute("d", "");
            }
          } else {
            path.setAttribute("d", "");
          }
          svg.appendChild(path);

          profileDiv.appendChild(svg);

          if (profile.label) {
            const label = document.createElement("div");
            label.textContent = profile.label;
            label.style.fontSize = "8px";
            profileDiv.appendChild(label);
          }

          div.appendChild(profileDiv);
        }
      });
    } catch {
      div.textContent = "Invalid profile data";
    }

    arg.rootElement.appendChild(div);
  },

  pdf: async (arg: {
    pdfDoc: import("pdf-lib").PDFDocument;
    page: import("pdf-lib").PDFPage;
    schema: {
      position: { x: number; y: number };
      width: number;
      height: number;
    };
    value: string;
  }) => {
    await drawProfilePreviews(arg);
  },

  propPanel: {
    schema: {},
    defaultSchema: {
      name: "profilePreview",
      type: "profilePreview",
      position: { x: 0, y: 0 },
      width: 190,
      height: 20,
    },
    defaultValue: "[]",
  },
};

export default profilePreviewSchema;
